import { all, put, select, take, takeEvery, call, fork } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';
import { v4 as uuidv4 } from 'uuid';
import spacetime from 'spacetime';
import { uniq } from 'ramda';

import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { createKnmDocumentTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmDocumentTypeSelectOption';
import {
  dictionariesFederalLawsGet,
  dictionariesItemValueGetSuccess,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { DictionariesActionsEnum } from 'CorePackage/DictionariesModule/enums/DictionariesActionsEnum';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { registryExportOkvedGet } from 'CorePackage/RegistryModule/actions/registryActions';
import { convertKnmDataViolatedRequirementsToFormValues } from 'KnmErknmPackage/MainModule/common/convertKnmDataViolatedRequirementsToFormValues';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import {
  knmGetSuccess,
  knmSetInitialAccessSettingsFormValues,
  knmSetPmIsUnableToRefuseFormValue,
  knmInfoGetWorkDaysCountBeforeStart,
  knmPatchRawData,
  knmAddVisibleModal,
} from 'KnmPackage/MainModule/actions/knmActions';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import { KnmActionsEnum } from 'KnmPackage/MainModule/enums/KnmActionsEnum';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IKnmErknmFormCollaboratingOrganization } from 'KnmPackage/MainModule/interfaces/IKnmErknmFormCollaboratingOrganization';
import {
  getKnmData,
  getKnmErknmParticipantPositionsSelectOptions,
  getKnmErknmPositionsSelectOptions,
  getKnmFormRef,
  getKnmTypesCanceled,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmErknmFormObject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { getDictionarySelectOptionWithState } from 'CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { getDictionariesItemValue } from 'CorePackage/DictionariesModule/common/getDictionariesItemValue';
import { IDictionaryPermitDocSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPermitDocSelectOption';
import { fetchCreatePmFromModalForm, fetchKnm } from 'APIPackage/KnmModule/services/knmApi';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { fetchKnmFindIndex } from 'APIPackage/KnmModule/services/knmListApi';
import { IKnmIndexPage } from 'APIPackage/KnmModule/interfaces/IKnmIndexPage';
import { IKnmIndex } from 'APIPackage/KnmModule/interfaces/IKnmIndex';
import { convertInitialReasonsPmToFormDate } from 'KnmPmPackage/PmReasonsModule/common/convertInitialReasonsPmToFormDate';
import { resultDecisionSubjectTypeOptions } from 'KnmPmPackage/PmResultOfInspection/components/PmResultOfInspectionPmResultDecisions';
import { createInteractionCategorySelectOption } from 'CorePackage/CatalogsModule/common/createInteractionCategorySelectOption';
import { IKnmErknmFormSuspensionEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSuspensionEvent';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { sortSuspensionEvents } from 'KnmErknmPackage/MainModule/common/sortSuspensionEvents';
import { convertSuspensionEventsToFormData } from 'KnmErknmPackage/MainModule/common/convertSuspensionEventsToFormData';
import { IKnmPmFormExtension } from 'KnmPmPackage/PmExtensionModule/interfaces/IKnmPmFormExtension';
import { convertEventExtensionToFormData } from 'KnmPmPackage/MainModule/common/convertEventExtensionToFormData';
import { appealStatusSelectOptions } from 'KnmErknmPackage/MainModule/constants/appealStatusSelectOptions';
import { ICatalog } from 'CorePackage/CatalogsModule/interfaces/ICatalog';
import { createKnmExpertTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmExpertTypeSelectOption';
import { getSelectOption } from 'KnmErknmPackage/MainModule/common/getSelectOption';
import { IKnmActSingerSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmActSingerSelectOption';
import { createSubmittedDocuments } from 'KnmPackage/MainModule/common/createSubmittedDocuments';
import { IKnmDictionary } from 'APIPackage/KnmModule/interfaces/IKnmDictionary';
import { IKnmRequirementsNpa } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IPm } from 'APIPackage/KnmModule/interfaces/IPm';
import { getOrganizations } from 'KnmErknmPackage/MainModule/sagas/getOrganizations';
import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import { convertKnmOkvedsFormValueToKnmData } from 'KnmPackage/MainModule/common/convertKnmOkvedsFormValueToKnmData';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IParticipantPosition } from 'APIPackage/DictionariesModule/interfaces/IParticipantPosition';
import { convertCreateModalPreventionToDTO } from 'KnmPackage/MainModule/common/convertCreateModalPreventionToDTO';
import { ROUTE_PM } from 'CorePackage/MainModule/routes/routes';
import { convertFederalLawToFormData } from 'KnmPackage/MainModule/common/convertFederalLawToFormData';
import { convertAgreementsDataToFormValues } from 'KnmErknmPackage/MainModule/common/convertAgreementsDataToFormValues';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { getProsecutorOrganization } from 'functions/getProsecutorOrganization';
import { IProsecutorOffice } from 'APIPackage/KnmModule/interfaces/IProsecutorOffice';
import {
  createDictionaryWaitChannel,
  dictionaryHandlerSaga,
  globalDictionaryTracker,
} from 'functions/dictionaryHandlerSaga';
import { scrollToBlock } from 'CorePackage/MainModule/common/scrollToBlock';
import { setInitHash } from 'CorePackage/HeaderModule/actions/headerActions';
import { getInitHash } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { convertInitialEventsPmDataToFormValues } from 'KnmPmPackage/PmEventsModule/common/convertInitialEventsPmDataToFormValues';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';

import { createKnmNotificationMethodSelectOption } from '../../../CorePackage/CatalogsModule/common/createKnmNotificationMethodSelectOption';
import { createPmCanceledReasonSelectOption } from '../../../CorePackage/CatalogsModule/common/createPmCanceledReasonSelectOption';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { IKnmErknmFormApproveDoc } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormApproveDoc';
import { IKnmNotificationsForm } from '../../../KnmPackage/KnmNotificationModule/interfaces/IKnmNotificationsForm';
import { convertKnmDataAccessPrivilegesToFormValues } from '../../../KnmPackage/MainModule/common/convertKnmDataAccessPrivilegesToFormValues';
import { IKnmPmPlanKnmSelectOption } from '../../PmReasonsModule/interfaces/IKnmPmPlanKnmSelectOption';
import { IPmRejectForm } from '../../PmRejectModule/interfaces/IPmRejectForm';
import {
  knmPmCreateFromModal,
  knmPmFillOrganizationsAndObjectsByOtherPassport,
  knmPmGetPlanKnmSelectOptions,
  knmPmSetChoosenOrganizationsByOtherPassport,
  knmPmSetInitialFormValues,
  knmPmSetPlanKnmSelectOptions,
} from '../actions/knmPmActions';
import { convertSubjectDataToFormValues } from '../../../KnmPackage/KnmSubjectModule/common/convertSubjectDataToformValues';
import { KnmPmActionsEnum } from '../enums/KnmPmActionsEnum';
import { getKnmPmInitialFormValues } from '../selectors/knmPmSelectors';
import { CatalogsActionsEnum } from '../../../CorePackage/CatalogsModule/enums/CatalogsActionsEnum';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getDistrictSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmDistrictSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { catalogGet, catalogGetSuccess } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { IKnmAbrogationReason } from '../../../APIPackage/CatalogsModule/interfaces/IKnmAbrogationReason';
import { IKnmLink } from '../../../APIPackage/KnmModule/interfaces/IKnmLink';
import { fetchKnmLinks } from '../../../APIPackage/KnmModule/services/knmAdditionalDataApi';
import { IReasonRequirements } from '../../../APIPackage/DictionariesModule/interfaces/IReasonRequirements';
import { IDictionaryVersion } from '../../../APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import { fetchDictionaryValue } from '../../../APIPackage/DictionariesModule/services/dictionariesApi';
import { parseDictionaryVersionValue } from '../../../CorePackage/DictionariesModule/common/parseDictionaryVersionValue';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { CHECKING_IT_ACCREDITATION } from '../../../CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { InspectionResultFieldEnum } from '../../../APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { IDictionaryPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { getDocTypeOption } from '../../../KnmPackage/KnmEventSubjectModule/common/docTypeGetFunctions';
import { getKindControlLabel } from '../../../KnmPackage/MainModule/common/getKindControlLabel';
import { getKindControlSelectedLabel } from '../../../KnmPackage/MainModule/common/getKindControlSelectedLabel';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { getSupervisionDictionaryValue } from '../../../CorePackage/DictionariesModule/common/getSupervisionDictionaryValue';
import { createKnmProsecutorOrganisationSelectOption } from '../../../KnmPackage/MainModule/common/createKnmProsecutorOrganisationSelectOption';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { fetchProsecutorOrganisationsByErvkUuidAndVersion } from '../../../APIPackage/CatalogsModule/services/prosecutorOrganisationsApi';
import { createUserOrganizationWithDistrictSelectOption } from '../../../CorePackage/AccountModule/common/createUserOrganizationWithDistrictSelectOption';
import { convertPmResultDecisionDataToPmForm } from './convertPmResultDecisionDataToPmForm';
import { convertPmActDataToPmForm } from './convertPmActDataToPmForm';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { IFederalDistrict } from '../../../APIPackage/KnmModule/interfaces/IFederalDistrict';
import { findRegionSelectOption } from '../../../KnmPackage/MainModule/components/regions';
import { IKnmPmForm } from '../interfaces/IKnmPmForm';
import {
  getInitialObject,
  knmPmInitFormValuesSagaHelper,
  processSuspensionEventsAndPeriodExtension,
} from '../common/knmPmInitFormValuesSagaHelpers';
import { convertSignersToFormValues } from '../../../KnmErknmPackage/MainModule/common/convertSignersToFormValues';
import { KnmReasonTypeNameEnum } from '../../../KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';
import { KnmModalsEnum } from '../../../KnmPackage/MainModule/enums/KnmModalsEnum';
import { convertObjectDataToFormValue } from '../../../KnmErknmPackage/MainModule/common/convertObjectDataToFormValue';

export function* watchKnmPmSaga() {
  yield takeEvery([KnmActionsEnum.KNM_GET_SUCCESS, KnmActionsEnum.KNM_UPDATE_SUCCESS], knmPmInit);
  yield takeEvery(
    [
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_SUCCESS,
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_ALREADY_LOAD,
    ],
    knmPmSetValueByOptionsSaga,
  );
  yield takeEvery(CatalogsActionsEnum.CATALOG_GET_SUCCESS, knmPmSetValueByOptionsFromCatalogActionsSaga);
  yield takeEvery(CatalogsActionsEnum.CATALOG_GET_SUCCESS, knmPmSetAbrogationReasonSaga);
  yield takeEvery(KnmPmActionsEnum.KNM_PM_GET_PLAN_KNM_SELECT_OPTIONS, knmPmGetPlanKnmSelectOptionsSaga);
  yield takeEvery(KnmPmActionsEnum.KNM_PM_RELOAD_PARTICIPANT_BY_CANCEL, knmPmReloadParticipantByCancelSaga);
  yield takeEvery(
    KnmPmActionsEnum.KNM_PM_FILL_ORGANIZATIONS_AND_OBJECTS_BY_OTHER_PASSPORT,
    pmErknmFillOrganizationsAndObjectsByOtherPassportSaga,
  );
  yield takeEvery(
    KnmPmActionsEnum.KNM_PM_SET_CHOOSEN_ORGANIZATION_BY_OTHER_PASSPORT,
    pmErknmSetChoosenOrganizationsByOtherPassportSaga,
  );
  yield takeEvery(KnmPmActionsEnum.KNM_PM_CREATE_FROM_MODAL, pmCreateFromModalSaga);
}

function* knmPmInitSelectOptionsSaga(action: ActionType<typeof knmGetSuccess>) {
  const { data: knmData } = action.payload;
  const knoOrganization = knmData?.pmErknm?.knoOrganization;
  const initialSupervisionType = knmData?.pmErknm?.supervisionType;
  const initialPmType = knmData?.pmErknm?.pmType;
  const initialSignerPosition = knmData.pmErknm?.signerPosition;
  const initialSigners = knmData.pmErknm?.signers;
  const inspectors = knmData?.pmErknm?.inspectors;
  const warningContentPositionCancel = knmData?.pmErknm?.warningContentPositionCancel;
  const initialReasons = knmData?.pmErknm?.reasons;
  const initialEvents = knmData?.pmErknm?.events;
  const initialObjects = knmData?.pmErknm?.objects;
  const collaboratingOrganizations = knmData?.pmErknm?.collaboratingOrganizations;
  const initialApproveDocs = knmData.pmErknm?.approveDocs;
  const accessPrivilege = knmData.pmErknm?.accessPrivilege;
  const pmResultDecisions = knmData.pmErknm?.subject?.pmResultDecisions;
  const pmAct = knmData.pmErknm?.subject?.act;
  const actResultContentPositionCancel = knmData?.pmErknm?.subject?.act?.resultContentPositionCancel;
  const initialFederalLaw = knmData.federalLaw;

  if (initialFederalLaw) {
    yield put(dictionariesFederalLawsGet());
  }
  const initialAgreements = knmData?.pmErknm?.agreements;
  const currentDictionariesFromData: IKnmDictionary[] = [];

  if (knmData?.pmErknm?.subject) {
    const { inn, ogrn } = knmData.pmErknm.subject;

    if (inn && ogrn) {
      yield put(registryExportOkvedGet({ inn, ogrn }));
    }
  }

  if (knoOrganization) {
    currentDictionariesFromData.push(knoOrganization);
  }
  if (initialSupervisionType) {
    if (knoOrganization) {
      yield getSupervisionDictionaryValue(knoOrganization, initialSupervisionType);
    } else {
      currentDictionariesFromData.push(initialSupervisionType);
    }
  }
  if (initialPmType) {
    currentDictionariesFromData.push(initialPmType);
  }
  if (initialSignerPosition) {
    currentDictionariesFromData.push(initialSignerPosition);
  }
  initialSigners?.forEach(signer => signer.titleSigner && currentDictionariesFromData.push(signer.titleSigner));
  if (warningContentPositionCancel) {
    currentDictionariesFromData.push(warningContentPositionCancel);
  }
  if (actResultContentPositionCancel) {
    currentDictionariesFromData.push(actResultContentPositionCancel);
  }

  const resultArrayActions: unknown[] = [];

  if (pmAct) {
    pmAct.signers?.forEach(signer => {
      signer.titleSigner && currentDictionariesFromData.push(signer.titleSigner);
    });
  }

  if (pmResultDecisions) {
    pmResultDecisions.forEach(item => {
      item.titleSigner && currentDictionariesFromData.push(item.titleSigner);
      item.signers?.forEach(signer => {
        signer.titleSigner && currentDictionariesFromData.push(signer.titleSigner);
      });
    });
  }
  if (inspectors) {
    inspectors?.forEach(item => item.position && currentDictionariesFromData.push(item.position));
  }
  initialReasons?.forEach(item => {
    item.riskIndicators?.forEach(
      indicator => indicator.riskIndicator && currentDictionariesFromData.push(indicator.riskIndicator),
    );
  });
  initialEvents?.forEach(item => item.event && currentDictionariesFromData.push(item.event));
  if (initialReasons) {
    yield knmPmInitKnmPlanByOptionsSaga();
  }
  initialObjects?.forEach(item => {
    if (item.objectType) {
      currentDictionariesFromData.push(item.objectType);
    }
    if (item.objectKind) {
      currentDictionariesFromData.push(item.objectKind);
    }
    if (item.objectSubKind) {
      currentDictionariesFromData.push(item.objectSubKind);
    }
    if (item.riskCategory) {
      currentDictionariesFromData.push(item.riskCategory);
    }
    if (item.dangerClass) {
      currentDictionariesFromData.push(item.dangerClass);
    }
  });
  collaboratingOrganizations?.forEach(item => item.organization && currentDictionariesFromData.push(item.organization));
  initialApproveDocs?.forEach(item => item.approveDoc && currentDictionariesFromData.push(item.approveDoc));
  accessPrivilege?.forEach(item =>
    currentDictionariesFromData.push({
      dictId: DictionaryUUIDsEnum.KNO_ORGANIZATIONS,
      dictVersionId: item.dictVersionId,
    }),
  );
  if (initialAgreements) {
    initialAgreements?.forEach(item => {
      item.titleSigner && currentDictionariesFromData.push(item.titleSigner);
      item.terminate?.titleSigner?.dictId && currentDictionariesFromData.push(item.terminate?.titleSigner);
      item.execution?.titleSigner && currentDictionariesFromData.push(item.execution?.titleSigner);
      item.changes?.forEach(change => change.titleSigner && currentDictionariesFromData.push(change.titleSigner));
    });
  }

  uniq(currentDictionariesFromData).forEach(item => {
    globalDictionaryTracker.addRequest(`${item.dictId}/${item.dictVersionId}`);
    resultArrayActions.push(getDictionariesItemValue(item));
  });

  yield all(resultArrayActions);
}

function* knmPmInitFormValuesSaga(action: ActionType<typeof knmGetSuccess>) {
  const { data: knmData } = action.payload;
  const state: IRootState = yield select();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { initialEntities, selectOptions, conditions } = knmPmInitFormValuesSagaHelper(knmData, state);

  if (knmData?.knmErknm) {
    yield put(
      knmSetInitialAccessSettingsFormValues({
        accessPrivilege: convertKnmDataAccessPrivilegesToFormValues(initialEntities.initialAccessPrivilege, state),
      }),
    );
  }

  // Проверяем поле knmErknm.reasons[0].reason.ervkUuid, чтобы загружать данные по IT-аккредитации или нет
  let reasonRequirementsValue: IReasonRequirements | undefined;

  if (
    initialEntities.initialReasons &&
    initialEntities.initialReasons.length > 0 &&
    initialEntities.initialReasons[0].reason?.ervkUuid
  ) {
    const dictionaryValue: IDictionaryVersion | undefined = yield fetchDictionaryValue(
      DictionaryUUIDsEnum.ERVK,
      initialEntities.initialReasons[0].reason?.ervkUuid,
    );

    // Значения ЕРВК может не оказаться в справочниках (get-dictionary-value отдаёт 200 с пустым телом) -
    // в этом случае пропускаем проверку IT-аккредитации, а не роняем инициализацию формы.
    if (dictionaryValue) {
      reasonRequirementsValue = parseDictionaryVersionValue<IReasonRequirements>(dictionaryValue);
    }

    if (reasonRequirementsValue && reasonRequirementsValue.it !== null) {
      const isPresentInItRegistry = !!knmData.pmErknm?.subject?.isPresentInItRegistry;

      if (
        (reasonRequirementsValue?.it && !isPresentInItRegistry) ||
        (!reasonRequirementsValue?.it && isPresentInItRegistry)
      ) {
        yield put(
          addNoticeAction({
            renderContent: CHECKING_IT_ACCREDITATION,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHECKING_IT_ACCREDITATION,
          }),
        );
      }
    }
  }

  const eventsPm = convertInitialEventsPmDataToFormValues(
    state,
    initialEntities.initialEvents,
    initialEntities.initialFormValues,
    selectOptions.uavTypeSelectOptions,
  );

  const { suspensionEvents, periodExtension } = processSuspensionEventsAndPeriodExtension(
    initialEntities.initialEvents,
    eventsPm,
    state,
  );

  yield put(knmSetPmIsUnableToRefuseFormValue(knmData.pmErknm?.isUnableToRefuse || undefined));

  if (knmData.startDate) {
    const formDate = spacetime(knmData.startDate || '');
    const currentDate = spacetime();

    if (formDate.isAfter(currentDate) || formDate.isSame(currentDate, 'date')) {
      yield put(knmInfoGetWorkDaysCountBeforeStart(knmData.startDate));
    }
  }

  const knmTypesCanceled: ICatalog<IKnmAbrogationReason[]> = yield select(getKnmTypesCanceled);

  const abrogationReasonId = parseInt(initialEntities.initialAbrogation?.reasonId || '0', 10);

  const abrogationReasonIdOption = knmTypesCanceled?.data?.length
    ? (() => {
        const item = knmTypesCanceled.data.find(i => i.id === abrogationReasonId);

        return item ? { ...item, label: item.name, value: item.code } : undefined;
      })()
    : undefined;

  const pmResultDecisions = convertPmResultDecisionDataToPmForm({
    pmResultDecisions: knmData.pmErknm?.subject?.pmResultDecisions,
    state,
    resultDecisionSubjectTypeOptions,
    subject: convertSubjectDataToFormValues(knmData.pmErknm?.subject),
  });

  const subjectFormValues = convertSubjectDataToFormValues(knmData.pmErknm?.subject);
  const objectsPm = initialEntities.initialObjects?.map(object => convertObjectDataToFormValue(object, state));

  const locationregion = (yield call(
    findRegionSelectOption,
    initialEntities.initialSubject?.locationAddress,
    initialEntities.initialSubject?.locationRegionCodeFromFns || initialEntities.initialSubject?.locationRegion,
    selectOptions.districtSelectOptions,
  )) as IKnmDistrictSelectOption | undefined;
  const locationfiasRegion = (yield call(
    findRegionSelectOption,
    initialEntities.initialSubject?.locationAddress,
    initialEntities.initialSubject?.locationRegionCodeFromFns || initialEntities.initialSubject?.locationFiasRegion,
    selectOptions.districtSelectOptions,
  )) as IKnmDistrictSelectOption | undefined;
  const region = (yield call(
    findRegionSelectOption,
    knmData.pmErknm?.manualAddress,
    knmData.pmErknm?.region,
    selectOptions.districtSelectOptions,
  )) as IKnmDistrictSelectOption | undefined;
  const fiasRegion = (yield call(
    findRegionSelectOption,
    knmData.pmErknm?.location,
    knmData.pmErknm?.fiasRegion,
    selectOptions.districtSelectOptions,
  )) as IKnmDistrictSelectOption | undefined;

  yield put(
    knmPmSetInitialFormValues({
      isTest: knmData.isTest || undefined,
      draft: knmData.draft || undefined,
      draftSavedAt: knmData.draftSavedAt || undefined,
      federalLaw: convertFederalLawToFormData(initialEntities.initialFederalLaw),
      reasonRequirements: reasonRequirementsValue,
      created: convertInitialKnmDateToFormDate(knmData.created, DATE_FORMAT_ISO_FNS),
      startDate: convertInitialKnmDateToFormDate(knmData.startDate, DATE_FORMAT_LONG_FNS),
      stopDate: convertInitialKnmDateToFormDate(knmData.stopDate, DATE_FORMAT_LONG_FNS),
      location: knmData.pmErknm?.location || undefined,
      originalStopDate: convertInitialKnmDateToFormDate(knmData.pmErknm?.originalStopDate, DATE_FORMAT_LONG_FNS),
      noteWarning: knmData.pmErknm?.noteWarning || undefined,
      noteWarningReactQuillEditor: handleReactQuillDeltaValue(
        knmData.pmErknm?.noteWarningReactQuillEditor || knmData.pmErknm?.noteWarning || undefined,
      ),
      contentWarningAttachments: knmData.pmErknm?.contentWarning?.attachments || undefined,
      objectionWarningAttachments: knmData.pmErknm?.objectionWarning?.attachments || undefined,
      exampleWarningAttachments: knmData.pmErknm?.exampleWarning?.attachments || undefined,

      ...subjectFormValues,
      locationregion,
      locationfiasRegion,

      isHasCollaboratingOrganization:
        typeof initialEntities.isHasCollaboratingOrganization === 'boolean'
          ? initialEntities.isHasCollaboratingOrganization
          : undefined,
      isHasNotification: booleanSelectOptions.find(
        (item: IBooleanSelectOption) => item.value === knmData.pmErknm?.isHasNotification,
      ),
      dateOfNotification: convertInitialKnmDateToFormDate(knmData.pmErknm?.dateOfNotification, DATE_FORMAT_LONG_FNS),
      resultOfInspection: knmData.pmErknm?.resultOfInspection || undefined,
      isRejectSubject: booleanSelectOptions.find(
        (item: IBooleanSelectOption) => item.value === knmData.pmErknm?.isRejectSubject,
      ),
      dateRejectSubject: convertInitialKnmDateToFormDate(knmData.pmErknm?.dateRejectSubject, DATE_FORMAT_LONG_FNS),

      documents: knmData.documents?.map(document => ({
        ...document,
        type: document?.type ? createKnmDocumentTypeSelectOption(document?.type) : undefined,
        attachments: document.attachments || undefined,
        listId: document.guid || uuidv4(),
      })),
      manualAddress: knmData.pmErknm?.manualAddress || undefined,
      region,
      fiasRegion,
      notInFIAS: knmData.pmErknm?.notInFIAS || false,
      matchesObjectAddress: knmData.pmErknm?.matchesObjectAddress || false,
      knoOrganizationPm: selectOptions.knoOrganizationSelectOption && {
        ...selectOptions.knoOrganizationSelectOption,
        value: {
          ...selectOptions.knoOrganizationSelectOption.value,
          title: selectOptions.knoOrganizationSelectOption.title || undefined,
        },
        inspectionResult: initialEntities.knoOrganization?.inspectionResult,
      },
      supervisionTypePm: selectOptions.supervisionTypeSelectOption
        ? {
            ...selectOptions.supervisionTypeSelectOption,
            value: {
              ...selectOptions.supervisionTypeSelectOption.value,
              title: selectOptions.supervisionTypeSelectOption.title,
            },
            inspectionResult: initialEntities.initialSupervisionType?.inspectionResult,
            label:
              getKindControlSelectedLabel(
                selectOptions.supervisionTypeSelectOption,
                conditions.isUseTransferringAuthorities,
              ) ||
              getKindControlLabel(selectOptions.supervisionTypeSelectOption, conditions.isUseTransferringAuthorities),
            selectedLabel: getKindControlSelectedLabel(
              selectOptions.supervisionTypeSelectOption,
              conditions.isUseTransferringAuthorities,
            ),
          }
        : undefined,
      pmType: selectOptions.pmTypeSelectOption && {
        ...selectOptions.pmTypeSelectOption,
        inspectionResult: initialEntities.initialPmType?.inspectionResult,
      },
      pmForm: selectOptions.pmFormSelectOption,
      interactionCategory: knmData.pmErknm?.interactionCategory
        ? {
            id: knmData.pmErknm?.interactionCategory.id,
            name: knmData.pmErknm?.interactionCategory.name,
            label: knmData.pmErknm?.interactionCategory.name,
            value: knmData.pmErknm?.interactionCategory.id,
            videoCallEnabled: knmData.pmErknm?.interactionCategory.videoCallEnabled,
          }
        : undefined,
      videoCallInfo:
        knmData.pmErknm?.videoCallInfo?.map(item => {
          return {
            ...item,
            showLinkLikeHypelink: true,
            listId: uuidv4(),
            dateTime:
              convertInitialKnmDateToFormDate(item.dateTime, DATE_FORMAT_LONG_MINUTE_FNS, DATE_FORMAT_ISO_FNS) || '',
          };
        }) || undefined,
      inspectorsErknm: initialEntities.initialInspectors?.map(item => {
        const inspectorPositionsSelectOption = getDictionarySelectOptionWithState<
          typeof DictionaryUUIDsEnum.PARTICIPANT_POSITIONS
        >(state, item?.position?.dictId, item?.position?.dictVersionId);

        const formInspector: IKnmErknmFormInspector = {
          ...item,
          position: inspectorPositionsSelectOption && {
            ...inspectorPositionsSelectOption,
            inspectionResult: item.position?.inspectionResult,
          },
          listId: item.guid || uuidv4(),
        };

        return formInspector;
      }),
      experts: initialEntities.initialExperts?.map(item => ({
        ...item,
        title: item.title || undefined,
        type: item.type ? createKnmExpertTypeSelectOption(item.type) : undefined,
        listId: item.guid || uuidv4(),
      })),
      reasonsPm: convertInitialReasonsPmToFormDate({ initialReasons: initialEntities.initialReasons, state }),
      eventsPm,
      suspensionEvents,
      isUnableToRefuse:
        typeof knmData.pmErknm?.isUnableToRefuse === 'boolean' ? knmData.pmErknm?.isUnableToRefuse : undefined,
      appealStatus: appealStatusSelectOptions.find(item => item.value === knmData.pmErknm?.appealStatus),
      objectsPm,
      collaboratingOrganizationsErknm: initialEntities.initialCollaboratingOrganizations?.map(item => {
        const collaboratingOrganizationsSelectOption = conditions.isOn411
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.KNO_ORGANIZATIONS>(
              state,
              item.organization?.dictId,
              item.organization?.dictVersionId,
            )
          : undefined;
        const formCollaboratingOrganization: IKnmErknmFormCollaboratingOrganization = {
          organization: collaboratingOrganizationsSelectOption,
          guid: item?.guid || undefined,
          dateCreate: item?.dateCreate || undefined,
          title: item?.title || undefined,
          ervkKnoOrganizationId: item?.ervkKnoOrganizationId || undefined,
          ervkTerritoryAgencyId: item?.ervkTerritoryAgencyId || undefined,
          ervkTerritoryAgencyVersionId: item?.ervkTerritoryAgencyVersionId || undefined,
          listId: item?.guid || uuidv4(),
        };

        return formCollaboratingOrganization;
      }),
      requirementsNpa: initialEntities.initialRequirementsNpa?.map(item => ({
        guid: null,
        npa: {
          id: item.npaId,
          number: item.number || undefined,
          acceptanceDate: formatDate({
            date: item.acceptanceDate ?? '',
            dateFormat: DATE_FORMAT_LONG_FNS,
            parseFormat: SYSTEM_DATE_FORMAT_FNS,
          }),
          title: item.title || undefined,
        },
        structuralUnit: {
          id: item.structuralUnit.id,
          title: item.structuralUnit.title || undefined,
        },
        mandatory: item.structuralUnit.mandatoryRequirements
          ? item.structuralUnit.mandatoryRequirements.map(mandatory => ({
              id: mandatory.id,
              title: mandatory.title || '',
            }))
          : [],
        isFilledFromLink: item.isFilledFromLink || undefined,
        inspectionResults: item.inspectionResults,
      })),
      approveDocs: initialEntities.initialApproveDocs?.map(item => {
        const approveDocSelectOption = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.ACCEPTED_DOCUMENT>(
          state,
          item.approveDoc?.dictId,
          item.approveDoc?.dictVersionId,
        );
        const formApproveDoc: IKnmErknmFormApproveDoc = {
          approveDoc: approveDocSelectOption && {
            ...approveDocSelectOption,
            inspectionResult: item.approveDoc?.inspectionResult,
          },
          numGuid: item.numGuid,
          dateCreate: item.dateCreate,
          listId: item.numGuid || uuidv4(),
        };

        return formApproveDoc;
      }),
      takenMeasures: initialEntities.initialTakenMeasures?.map(item => ({
        guid: item.guid || undefined,
        value: item.value || undefined,
      })),
      docTarget: initialEntities.initialDocTarget?.map(doc => {
        const selectOption: IDictionaryPermitDocSelectOption | undefined = !doc.ervkControlKindDocTarget?.targetTitle
          ? undefined
          : {
              type: doc.ervkControlKindDocTarget.type,
              targetId: doc.ervkControlKindDocTarget.targetId,
              targetTitle: doc.ervkControlKindDocTarget.targetTitle,
              id: doc.ervkControlKindDocTarget?.targetId,
              label: doc.ervkControlKindDocTarget?.targetTitle,
              value: {
                dictId: DictionaryUUIDsEnum.PERMIT_DOCS,
                dictVersionId: '',
              },
            };

        return {
          ervkControlKindDocTarget: selectOption,
          specificRequirementAndClause: doc.specificRequirementAndClause || undefined,
          file: doc.file ? [doc.file] : undefined,
          listId: uuidv4(),
          docTargetType: getDocTypeOption(doc.ervkControlKindDocTarget?.type || ''),
          guid: doc.guid,
          inspectionResult: doc.inspectionResult || undefined,
        };
      }),
      subjectNotifications:
        initialEntities.initialNotifications?.map(x => {
          const notification: IKnmNotificationsForm = {
            date: convertInitialKnmDateToFormDate(x.date, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
            type: x.type ? createKnmNotificationMethodSelectOption(x.type) : undefined,
            comment: x.comment,
            listId: x.guid || uuidv4(),
            isDateEmpty: !x.date,
            isTypeEmpty: !x.type,
            isCommentEmpty: !x.comment,
            isNew: !!x.isNew,
          };

          return notification;
        }) || undefined,
      pmReject:
        initialEntities.initialReject?.map(x => {
          const rejectForm: IPmRejectForm = {
            date: x.date,
            listId: x.guid || uuidv4(),
            isNew: !!x.isNew,
            canceledReason: x.pmCanceledReason ? createPmCanceledReasonSelectOption(x.pmCanceledReason) : undefined,
          };

          return rejectForm;
        }) || undefined,
      officialFullName: knmData.pmErknm?.officialFullName || '',
      warningContentPositionCancel: selectOptions.warningContentPositionCancelSelectOption,
      pmSignerPosition: !selectOptions.signerPositionSelectOption
        ? undefined
        : {
            ...selectOptions.signerPositionSelectOption,
            inspectionResult: initialEntities.initialSignerPosition?.inspectionResult,
          },
      signers: convertSignersToFormValues(knmData?.pmErknm?.signers, state, undefined),
      warningContentCreatorPosition: knmData.pmErknm?.warningContentCreatorPosition || '',
      extractVersion: knmData.pmErknm?.extractVersion || '',
      officialFullNameCancel: knmData.pmErknm?.officialFullNameCancel || '',
      cancellationAttachment: knmData.pmErknm?.cancellationAttachment || undefined,
      cancellationComment: knmData.pmErknm?.cancellationComment || undefined,
      rescheduling: !initialEntities.initialRescheduling
        ? null
        : initialEntities.initialRescheduling.map(reschedule => {
            return {
              isNew: !!reschedule.isNew,
              guid: reschedule.guid || undefined,
              newStartDate: convertInitialKnmDateToFormDate(reschedule.newStartDate, DATE_FORMAT_LONG_FNS),
              initiationDate: convertInitialKnmDateToFormDate(reschedule.initiationDate, DATE_FORMAT_LONG_FNS),
              newEndDate: convertInitialKnmDateToFormDate(reschedule.newEndDate, DATE_FORMAT_LONG_FNS),
              comment: reschedule.comment || undefined,
              oldStartDate: convertInitialKnmDateToFormDate(reschedule.oldStartDate, DATE_FORMAT_LONG_FNS),
              oldEndDate: convertInitialKnmDateToFormDate(reschedule.oldEndDate, DATE_FORMAT_LONG_FNS),
            };
          }),
      abrogation: !initialEntities.initialAbrogation
        ? null
        : {
            comment: initialEntities.initialAbrogation.comment,
            reasonId: abrogationReasonIdOption,
            reasonFile: initialEntities.initialAbrogation.reasonFile
              ? [initialEntities.initialAbrogation.reasonFile]
              : undefined,
          },
      isViolatedMandatoryRequirements: booleanSelectOptions.find(item => item.value === knmData.pmErknm?.isViolations),
      violatedMandatoryRequirements: knmData.pmErknm?.violations
        ? convertKnmDataViolatedRequirementsToFormValues(knmData.pmErknm.violations, eventsPm)
        : undefined,
      withoutIssue: typeof knmData.pmErknm?.withoutIssue === 'boolean' ? knmData.pmErknm?.withoutIssue : undefined,
      pmResultDecisions,
      act: convertPmActDataToPmForm({
        act: knmData.pmErknm?.subject?.act,
        state,
        subject: convertSubjectDataToFormValues(knmData.pmErknm?.subject),
        passportEvents: eventsPm,
      }),
      periodExtension,
      prosecutorOrganizationErknm:
        (initialEntities.initialProsecutorOrganization?.id ||
          initialEntities.initialProsecutorOrganization?.ervkUuid) &&
        initialEntities.initialProsecutorOrganization?.name
          ? createKnmProsecutorOrganisationSelectOption({
              prosecutorOrganization: {
                ...initialEntities.initialProsecutorOrganization,
                id:
                  initialEntities.initialProsecutorOrganization?.id != null
                    ? initialEntities.initialProsecutorOrganization.id
                    : initialEntities.initialProsecutorOrganization.ervkUuid || '',
                name: initialEntities.initialProsecutorOrganization?.name,
              },
              districtName: null,
              districtId: null,
            })
          : undefined,
      agreements: convertAgreementsDataToFormValues(
        initialEntities.initialAgreements,
        state,
        undefined,
        pmResultDecisions,
      ),
      agreementOnApproval: initialEntities.initialAgreementOnApproval || undefined,
      repeatPassports: knmData.pmErknm?.repeatPassports || undefined,
    }),
  );
}

function* knmPmInit(action: ActionType<typeof knmGetSuccess>) {
  if (action.payload.data.pmErknm) {
    const state: IRootState = yield select();
    const initHash = getInitHash(state);

    yield put(coreSetLoading(true));

    // Сбрасываем трекер справочников, чтобы «зависшие» с прошлого открытия
    // запросы не блокировали ожидание загрузки на 30 секунд
    globalDictionaryTracker.reset();

    yield fork(dictionaryHandlerSaga);
    yield knmPmInitFormValuesSaga(action);
    yield knmPmInitSelectOptionsSaga(action);
    yield knmPmInitLinksSaga(action);
    yield knmPmSetValueByOptionsFromCatalogActionsSaga({
      type: CatalogsActionsEnum.CATALOG_GET_SUCCESS,
      payload: { catalogName: CommonCatalogsNamesEnum.DISTRICTS, data: [] },
    });
    yield put(catalogGet(CommonCatalogsNamesEnum.KNM_TYPES_CANCELED));
    yield knmPmInitProsecutorOrganizationSaga(action);
    yield knmPmFillAgreementProsecutorOrganizationSaga(action);
    if (initHash) {
      scrollToBlock(initHash);
      yield put(setInitHash(undefined));
    }

    yield put(coreSetLoading(false));
  }
}

// function* knmPmInit(action: ActionType<typeof knmGetSuccess>) {
//   if (action.payload.data.version === KnmVersionsEnum.ERKNM && action.payload.data.pmErknm) {
//     yield put(knmSetLoading(true));
//     yield knmPmInitFormValuesSaga(action);
//     yield knmPmInitSelectOptionsSaga(action);
//     yield knmPmInitLinksSaga(action);
//     yield knmPmSetValueByOptionsFromCatalogActionsSaga({
//       type: CatalogsActionsEnum.CATALOG_GET_SUCCESS,
//       payload: { catalogName: CommonCatalogsNamesEnum.DISTRICTS, data: [] },
//     });
//     yield put(catalogGet(CommonCatalogsNamesEnum.KNM_TYPES_CANCELED));
//     yield knmPmInitProsecutorOrganizationSaga(action);
//     yield put(knmSetLoading(false));
//   }
// }

function* knmPmInitLinksSaga(action: ActionType<typeof knmGetSuccess>) {
  function* funcTry() {
    const {
      data: { id },
    } = action.payload;

    const links: IKnmLink[] = yield fetchKnmLinks(id);

    const partialInitialFormValues: {
      links: IKnmPmForm['links'];
    } = {
      links,
    };

    yield put(knmPmSetInitialFormValues(partialInitialFormValues));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmPmSetValueByOptionsSaga(action: ActionType<typeof dictionariesItemValueGetSuccess>) {
  const { dictionaryUUID, dictionaryVersionUUID } = action.payload;
  const state: IRootState = yield select();
  const currentKnmData = getKnmData(state);
  const initialFormValues = getKnmPmInitialFormValues(state);
  const settings = getAdminFetchSettings(state);

  switch (dictionaryUUID) {
    case DictionaryUUIDsEnum.KNO_ORGANIZATIONS: {
      const knmDataKnoOrganizationErknm = currentKnmData?.pmErknm?.knoOrganization;

      if (dictionaryVersionUUID === knmDataKnoOrganizationErknm?.dictVersionId) {
        const selectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
          state,
          dictionaryUUID,
          dictionaryVersionUUID,
        );
        const partialInitialFormValues: {
          knoOrganizationPm: IKnmPmForm['knoOrganizationPm'];
        } = {
          knoOrganizationPm: selectOption && {
            ...selectOption,
            value: {
              ...selectOption.value,
              title: selectOption.title || undefined,
            },
            inspectionResult: currentKnmData?.pmErknm?.knoOrganization?.inspectionResult,
          },
        };

        yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      }
      const initialCollaboratingOrganizations = currentKnmData?.pmErknm?.collaboratingOrganizations;

      const isOn411 = isLaterThanAdminSetting(
        initialFormValues.created,
        settings,
        AdminSettingCodesEnum.COLLABORATION_KNO_ERVK_SEARCH_4_1_1,
      );

      const partialInitialFormValues: {
        collaboratingOrganizationsErknm: IKnmPmForm['collaboratingOrganizationsErknm'];
      } = {
        collaboratingOrganizationsErknm: initialCollaboratingOrganizations?.map(item => {
          const collaboratingOrganizationsSelectOption = isOn411
            ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.KNO_ORGANIZATIONS>(
                state,
                item.organization?.dictId,
                item.organization?.dictVersionId,
              )
            : undefined;
          const formCollaboratingOrganization: IKnmErknmFormCollaboratingOrganization = {
            organization: collaboratingOrganizationsSelectOption,
            guid: item?.guid || undefined,
            dateCreate: item?.dateCreate || undefined,
            title: item?.title || undefined,
            ervkKnoOrganizationId: item?.ervkKnoOrganizationId || undefined,
            ervkTerritoryAgencyId: item?.ervkTerritoryAgencyId || undefined,
            ervkTerritoryAgencyVersionId: item?.ervkTerritoryAgencyVersionId || undefined,
            listId: item?.guid || uuidv4(),
          };

          return formCollaboratingOrganization;
        }),
      };

      yield put(knmPmSetInitialFormValues(partialInitialFormValues));

      const initialAccessPrivilege = currentKnmData?.pmErknm?.accessPrivilege;

      if (currentKnmData?.pmErknm) {
        yield put(
          knmSetInitialAccessSettingsFormValues({
            accessPrivilege: convertKnmDataAccessPrivilegesToFormValues(initialAccessPrivilege, state),
          }),
        );
      }
      break;
    }
    case DictionaryUUIDsEnum.SUPERVISION_TYPE: {
      const initialSupervisionType = currentKnmData?.pmErknm?.supervisionType;

      if (dictionaryVersionUUID === initialSupervisionType?.dictVersionId) {
        const supervisionTypeSelectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
          state,
          dictionaryUUID,
          dictionaryVersionUUID,
        );

        const isUseTransferringAuthorities = isLaterThanAdminSetting(
          initialFormValues.created,
          settings,
          AdminSettingCodesEnum.TRANSFERRING_AUTHORITIES_4_1_22_STARTS_FROM,
        );

        const partialInitialFormValues: {
          supervisionTypePm: IKnmPmForm['supervisionTypePm'];
        } = {
          supervisionTypePm: supervisionTypeSelectOption
            ? {
                ...supervisionTypeSelectOption,
                value: {
                  ...supervisionTypeSelectOption.value,
                  title: supervisionTypeSelectOption.title,
                },
                inspectionResult: initialSupervisionType?.inspectionResult,
                label:
                  getKindControlSelectedLabel(supervisionTypeSelectOption, isUseTransferringAuthorities) ||
                  getKindControlLabel(supervisionTypeSelectOption, isUseTransferringAuthorities),
                selectedLabel: getKindControlSelectedLabel(supervisionTypeSelectOption, isUseTransferringAuthorities),
              }
            : undefined,
        };

        yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      }
      break;
    }
    case DictionaryUUIDsEnum.PROF_KIND: {
      const initialPmType = currentKnmData?.pmErknm?.pmType;

      if (dictionaryVersionUUID === initialPmType?.dictVersionId) {
        const pmTypeSelectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
          state,
          dictionaryUUID,
          dictionaryVersionUUID,
        );

        const partialInitialFormValues: {
          pmType: IKnmPmForm['pmType'];
        } = {
          pmType: pmTypeSelectOption && {
            ...pmTypeSelectOption,
            inspectionResult: initialPmType?.inspectionResult,
          },
        };

        yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      }
      break;
    }
    case DictionaryUUIDsEnum.PARTICIPANT_POSITIONS: {
      const initialInspectors = currentKnmData?.pmErknm?.inspectors;
      const signerPosition = currentKnmData?.pmErknm?.signerPosition;
      const initialSigners = currentKnmData?.pmErknm?.signers;
      const positions = getKnmErknmParticipantPositionsSelectOptions(state);

      /*
        В течение недели на проде значения для поля "Должность лица, подписавшего паспорт ПМ" заполнялись из неправильного справочника,
        поэтому было принято решение оставить реализацию заполнения поля из словаря participant_positions, чтобы не сломать отображение в уже заполненных ПМ
       */
      let signerPositionValue: IDictionaryPositionSelectOption | undefined = getDictionarySelectOptionWithState<
        typeof dictionaryUUID
      >(state, signerPosition?.dictId, signerPosition?.dictVersionId);

      if (!signerPositionValue) {
        signerPositionValue = positions?.find(
          i => i.value.dictVersionId === signerPosition?.dictVersionId && i.value.dictId === signerPosition?.dictId,
        );
      }

      let fioAndPositionSinger: IKnmActSingerSelectOption | undefined;

      if (currentKnmData?.pmErknm?.subject?.act?.titleSigner) {
        const titleActSignerSelectOption = currentKnmData?.pmErknm?.subject?.act?.titleSigner.dictId
          ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
              state,
              currentKnmData?.pmErknm?.subject?.act?.titleSigner.dictId,
              currentKnmData?.pmErknm?.subject?.act?.titleSigner.dictVersionId,
            )
          : undefined;

        if (titleActSignerSelectOption) {
          fioAndPositionSinger = {
            id: '',
            label: `${currentKnmData?.pmErknm?.subject?.act.fioSigner} - ${titleActSignerSelectOption?.label}`,
            value: {
              dateCreate: null,
              fullName: currentKnmData?.pmErknm?.subject?.act.fioSigner || '',
              guid: undefined,
              listId: '',
              position: titleActSignerSelectOption,
              label: '',
            },
          };
        }
      }

      let knoInspectorsOptions: IKnmErknmFormInspector[] | undefined;

      if (currentKnmData?.pmErknm?.subject?.act?.knoInspectors?.length) {
        const inspectors = currentKnmData?.pmErknm?.subject?.act?.knoInspectors?.map(knoInspector => {
          const positionSelectOption = knoInspector.position?.dictId
            ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
                state,
                knoInspector.position?.dictId,
                knoInspector.position?.dictVersionId,
              ) ||
              getSelectOption<DictionaryUUIDsEnum.POSITIONS>(
                state,
                knoInspector.position?.dictId,
                knoInspector.position?.dictVersionId,
              )
            : undefined;
          const formKnoInspector: IKnmErknmFormInspector = {
            ...knoInspector,
            position: positionSelectOption,
            listId: knoInspector.guid || uuidv4(),
            label: `${knoInspector.fullName} - ${positionSelectOption?.label}`,
          };

          return formKnoInspector;
        });

        knoInspectorsOptions = inspectors?.map(i => ({
          listId: i.guid || uuidv4(),
          guid: i.guid || undefined,
          knoInspector: {
            id: i?.guid || '',
            label: `${i.fullName} - ${i.position?.label}`,
            value: i,
          },
          fullName: undefined,
          position: undefined,
          dateCreate: i.dateCreate || undefined,
        }));
      }

      let actResultContentPositionCancelSelectOption: IDictionarySelectOption<IParticipantPosition> | undefined;

      if (currentKnmData?.pmErknm?.subject?.act?.resultContentPositionCancel) {
        actResultContentPositionCancelSelectOption = currentKnmData?.pmErknm?.subject?.act?.resultContentPositionCancel
          .dictId
          ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
              state,
              currentKnmData?.pmErknm?.subject?.act?.resultContentPositionCancel.dictId,
              currentKnmData?.pmErknm?.subject?.act?.resultContentPositionCancel.dictVersionId,
            )
          : undefined;
      }

      if (initialFormValues.act) {
        const partialInitialFormValues: {
          act: IKnmPmForm['act'];
        } = {
          act: {
            ...initialFormValues.act,
            fioAndPositionSinger,
            knoInspectors: knoInspectorsOptions,
            resultContentPositionCancel: actResultContentPositionCancelSelectOption,
            signers: convertSignersToFormValues(
              currentKnmData?.pmErknm?.subject?.act?.signers,
              state,
              initialInspectors || [],
            ),
          },
        };

        yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      }

      if (signerPositionValue || initialInspectors || initialSigners) {
        const initialPmResultDecisions = currentKnmData?.pmErknm?.subject?.pmResultDecisions;

        const partialInitialFormValues: {
          pmSignerPosition: IKnmPmForm['pmSignerPosition'];
          inspectorsErknm: IKnmPmForm['inspectorsErknm'];
          pmResultDecisions: IKnmPmForm['pmResultDecisions'];
          signers: IKnmPmForm['signers'];
        } = {
          pmSignerPosition: !signerPositionValue
            ? undefined
            : {
                ...signerPositionValue,
                inspectionResult: signerPosition?.inspectionResult,
              },
          inspectorsErknm: initialInspectors?.map((item, index) => {
            const inspectorPositionsSelectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
              state,
              item.position?.dictId,
              item.position?.dictVersionId,
            );

            const formInspector: IKnmErknmFormInspector = {
              ...item,
              ...initialFormValues.inspectorsErknm?.[index],
              position: inspectorPositionsSelectOption && {
                ...inspectorPositionsSelectOption,
                inspectionResult: item.position?.inspectionResult,
              },
              listId: item.guid || uuidv4(),
              // isNew: false,
              //   firstExtractDateTime:
              //     convertInitialKnmDateToFormDate(
              //       item.firstExtractDateTime,
              //       DATE_FORMAT_LONG_MINUTE_FNS,
              //       DATE_FORMAT_ISO_FNS,
              //     ) || null,
              //   systemSignatureUsed: item.systemSignatureUsed || false,
              //   systemSignatureTitle: item.systemSignatureTitle || null,
              //   systemSignatureMnemonic: item.systemSignatureMnemonic || null,
              //   systemSignatureDateTime: item.systemSignatureDateTime || null,
            };

            return formInspector;
          }),
          pmResultDecisions: initialPmResultDecisions?.map((item, index) => ({
            ...initialFormValues.pmResultDecisions?.[index],
            signerPosition: getDictionarySelectOptionWithState<typeof dictionaryUUID>(
              state,
              item.titleSigner?.dictId,
              item.titleSigner?.dictVersionId,
            ),
            cancelTitleSigner: getDictionarySelectOptionWithState<typeof dictionaryUUID>(
              state,
              item.cancelTitleSigner?.dictId,
              item.cancelTitleSigner?.dictVersionId,
            ),
            listId: item.guid || uuidv4(),
            isNew: false,
            firstExtractDateTime: item.firstExtractDateTime,
            systemSignatureUsed: item.systemSignatureUsed || false,
            systemSignatureTitle: item.systemSignatureTitle || null,
            systemSignatureMnemonic: item.systemSignatureMnemonic || null,
            systemSignatureDateTime: item.systemSignatureDateTime || null,
            signers: convertSignersToFormValues(item.signers, state, undefined),
          })),
          signers: convertSignersToFormValues(initialSigners, state, undefined),
        };

        yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      }
      break;
    }
    case DictionaryUUIDsEnum.POSITIONS: {
      const initialSignerPosition = currentKnmData?.pmErknm?.signerPosition;
      const warningContentPositionCancel = currentKnmData?.pmErknm?.warningContentPositionCancel;
      const initialSigners = currentKnmData?.pmErknm?.signers;

      if (initialSigners) {
        const signers = convertSignersToFormValues(initialSigners, state, undefined);

        yield put(
          knmPmSetInitialFormValues({
            signers,
          }),
        );
      }

      if (dictionaryVersionUUID === warningContentPositionCancel?.dictVersionId) {
        const positionSelectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
          state,
          warningContentPositionCancel.dictId,
          warningContentPositionCancel.dictVersionId,
        );

        const partialInitialFormValues: { warningContentPositionCancel: IKnmPmForm['warningContentPositionCancel'] } = {
          warningContentPositionCancel: positionSelectOption,
        };

        yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      }

      if (dictionaryVersionUUID === initialSignerPosition?.dictVersionId) {
        const signerPositionSelectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
          state,
          initialSignerPosition.dictId,
          initialSignerPosition.dictVersionId,
        );

        const partialInitialFormValues: { pmSignerPosition: IKnmPmForm['pmSignerPosition'] } = {
          pmSignerPosition: !signerPositionSelectOption
            ? undefined
            : {
                ...signerPositionSelectOption,
                inspectionResult: initialSignerPosition?.inspectionResult,
              },
        };

        yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      }

      let knoInspectorsOptions: IKnmErknmFormInspector[] | undefined;

      if (currentKnmData?.pmErknm?.subject?.act?.knoInspectors?.length) {
        const inspectors = currentKnmData?.pmErknm?.subject?.act?.knoInspectors?.map(knoInspector => {
          const positionSelectOption = knoInspector.position?.dictId
            ? getSelectOption<DictionaryUUIDsEnum.POSITIONS>(
                state,
                knoInspector.position?.dictId,
                knoInspector.position?.dictVersionId,
              ) ||
              getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
                state,
                knoInspector.position?.dictId,
                knoInspector.position?.dictVersionId,
              )
            : undefined;
          const formKnoInspector: IKnmErknmFormInspector = {
            ...knoInspector,
            position: positionSelectOption,
            listId: knoInspector.guid || uuidv4(),
            label: `${knoInspector.fullName} - ${positionSelectOption?.label}`,
          };

          return formKnoInspector;
        });

        knoInspectorsOptions = inspectors?.map(i => ({
          listId: i.guid || uuidv4(),
          guid: i.guid || undefined,
          knoInspector: {
            id: i?.guid || '',
            label: `${i.fullName} - ${i.position?.label}`,
            value: i,
          },
          fullName: undefined,
          position: undefined,
          dateCreate: i.dateCreate || undefined,
        }));
      }

      if (initialFormValues.act) {
        const initialInspectors = currentKnmData?.pmErknm?.inspectors;
        const partialActInitialFormValues: {
          act: IKnmPmForm['act'];
        } = {
          act: {
            ...initialFormValues.act,
            knoInspectors: knoInspectorsOptions,
            signers: convertSignersToFormValues(
              currentKnmData?.pmErknm?.subject?.act?.signers,
              state,
              initialInspectors || [],
            ),
          },
        };

        yield put(knmPmSetInitialFormValues(partialActInitialFormValues));
      }

      if (initialFormValues.agreements || initialFormValues.pmResultDecisions) {
        const pmResultDecisions = convertPmResultDecisionDataToPmForm({
          pmResultDecisions: currentKnmData?.pmErknm?.subject?.pmResultDecisions,
          state,
          resultDecisionSubjectTypeOptions,
          subject: convertSubjectDataToFormValues(currentKnmData?.pmErknm?.subject),
        });

        const partialInitialFormValues: {
          agreements: IKnmPmForm['agreements'];
          pmResultDecisions: IKnmPmForm['pmResultDecisions'];
        } = {
          agreements: convertAgreementsDataToFormValues(
            currentKnmData?.pmErknm?.agreements,
            state,
            undefined,
            pmResultDecisions,
          ),
          pmResultDecisions,
        };

        yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      }
      break;
    }
    case DictionaryUUIDsEnum.RISK_INDICATORY: {
      const initialReasons = currentKnmData?.pmErknm?.reasons;

      const partialInitialFormValues: {
        reasonsPm: IKnmPmForm['reasonsPm'];
      } = {
        reasonsPm: convertInitialReasonsPmToFormDate({ initialReasons, state, initialFormValues }),
      };

      yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      break;
    }
    case DictionaryUUIDsEnum.EVENT_TYPE: {
      const initialEvents = currentKnmData?.pmErknm?.events;

      const suspensionEvents: IKnmErknmFormSuspensionEvent[] = [];
      let periodExtension: IKnmPmFormExtension | undefined;
      const eventsPm = initialEvents?.map((item, index) => {
        const selectOption = item.event?.dictId
          ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
              state,
              item.event?.dictId,
              item.event?.dictVersionId,
            )
          : undefined;
        const formEvent: IKnmErknmFormEvent = {
          guid: undefined,
          startDate: undefined,
          stopDate: undefined,
          dateCreate: undefined,
          interactionCategory: undefined,
          listId: item?.guid || uuidv4(),
          ...initialFormValues.eventsPm?.[index],
          type: selectOption,
          isNew: !!item?.isNew, // false?
          isFilledFromLink: item.isFilledFromLink || undefined,
          eventCopyNumber: item.eventCopyNumber || undefined,
          experts: undefined,
        };

        suspensionEvents.push(...convertSuspensionEventsToFormData(item, formEvent, index, state));

        if (selectOption?.title === 'Инструментальное обследование') {
          periodExtension = convertEventExtensionToFormData(item.periodExtension);
        }

        return formEvent;
      });

      sortSuspensionEvents(suspensionEvents);

      const subject = convertSubjectDataToFormValues(currentKnmData?.pmErknm?.subject);

      yield put(
        knmPmSetInitialFormValues({
          eventsPm,
          suspensionEvents,
          periodExtension,
          act: convertPmActDataToPmForm({
            act: currentKnmData?.pmErknm?.subject?.act,
            state,
            subject,
            passportEvents: eventsPm,
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.SUBJECT_TYPE: {
      const initialObjects = currentKnmData?.pmErknm?.objects;

      const partialInitialFormValues: {
        objectsPm: IKnmPmForm['objectsPm'];
      } = {
        objectsPm: initialObjects?.map((object, index) => {
          const objectTypeSelectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
            state,
            object.objectType?.dictId,
            object.objectType?.dictVersionId,
          );
          const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsPm?.[index];
          const inspectionResults = object.inspectionResults || [];
          const parentInspectionResult = inspectionResults.find(
            i => i.inspectedField === InspectionResultFieldEnum.OBJECT_TYPE,
          );

          return {
            ...getInitialObject(initialObject),
            tmpErvkCosId: object.tmpErvkCosId || undefined,
            objectType: objectTypeSelectOption && {
              ...objectTypeSelectOption,
              inspectionResult: object.objectType?.inspectionResult || parentInspectionResult,
            },
          };
        }),
      };

      yield put(knmPmSetInitialFormValues(partialInitialFormValues));

      break;
    }
    case DictionaryUUIDsEnum.SUBJECT_KIND: {
      const initialObjects = currentKnmData?.pmErknm?.objects;

      const partialInitialFormValues: {
        objectsPm: IKnmPmForm['objectsPm'];
      } = {
        objectsPm: initialObjects?.map((object, index) => {
          const objectKindSelectOption = object.objectKind?.dictId
            ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                state,
                object.objectKind?.dictId,
                object.objectKind?.dictVersionId,
              )
            : undefined;
          const objectSubKindSelectOption = object.objectSubKind?.dictId
            ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                state,
                object.objectSubKind?.dictId,
                object.objectSubKind?.dictVersionId,
              )
            : undefined;
          const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsPm?.[index];
          const inspectionResults = object.inspectionResults || [];
          const parentInspectionResultKind = inspectionResults.find(
            i => i.inspectedField === InspectionResultFieldEnum.OBJECT_KIND,
          );
          const parentInspectionResultSubKind = inspectionResults.find(
            i => i.inspectedField === InspectionResultFieldEnum.OBJECT_SUB_KIND,
          );

          return {
            ...getInitialObject(initialObject),
            tmpErvkCosId: object.tmpErvkCosId || undefined,
            objectKind: objectKindSelectOption && {
              ...objectKindSelectOption,
              inspectionResult: object.objectKind?.inspectionResult || parentInspectionResultKind,
            },
            objectSubKind: objectSubKindSelectOption && {
              ...objectSubKindSelectOption,
              inspectionResult: object.objectSubKind?.inspectionResult || parentInspectionResultSubKind,
            },
          };
        }),
      };

      yield put(knmPmSetInitialFormValues(partialInitialFormValues));

      break;
    }
    case DictionaryUUIDsEnum.SUBJECT_SUB_KIND: {
      const initialObjects = currentKnmData?.pmErknm?.objects;

      const partialInitialFormValues: {
        objectsPm: IKnmPmForm['objectsPm'];
      } = {
        objectsPm: initialObjects?.map((object, index) => {
          const objectKindSelectOption = object.objectKind?.dictId
            ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                state,
                object.objectKind?.dictId,
                object.objectKind?.dictVersionId,
              )
            : undefined;
          const objectSubKindSelectOption = object.objectSubKind?.dictId
            ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                state,
                object.objectSubKind?.dictId,
                object.objectSubKind?.dictVersionId,
              )
            : undefined;
          const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsPm?.[index];
          const inspectionResults = object.inspectionResults || [];
          const parentInspectionResultKind = inspectionResults.find(
            i => i.inspectedField === InspectionResultFieldEnum.OBJECT_KIND,
          );
          const parentInspectionResultSubKind = inspectionResults.find(
            i => i.inspectedField === InspectionResultFieldEnum.OBJECT_SUB_KIND,
          );

          return {
            ...getInitialObject(initialObject),
            tmpErvkCosId: object.tmpErvkCosId || undefined,
            objectKind: objectKindSelectOption && {
              ...objectKindSelectOption,
              inspectionResult: object.objectKind?.inspectionResult || parentInspectionResultKind,
            },
            objectSubKind: objectSubKindSelectOption && {
              ...objectSubKindSelectOption,
              inspectionResult: object.objectSubKind?.inspectionResult || parentInspectionResultSubKind,
            },
          };
        }),
      };

      yield put(knmPmSetInitialFormValues(partialInitialFormValues));

      break;
    }
    case DictionaryUUIDsEnum.RISK_CATEGORY: {
      const initialObjects = currentKnmData?.pmErknm?.objects;

      const partialInitialFormValues: {
        objectsPm: IKnmPmForm['objectsPm'];
      } = {
        objectsPm: initialObjects?.map((object, index) => {
          const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsPm?.[index];
          const inspectionResults = object.inspectionResults || [];
          const parentInspectionResultRisk = inspectionResults.find(
            i => i.inspectedField === InspectionResultFieldEnum.RISK_CATEGORY,
          );
          const parentInspectionResultDanger = inspectionResults.find(
            i => i.inspectedField === InspectionResultFieldEnum.DANGER_CLASS,
          );
          const riskCategorySelectOption = object.riskCategory?.dictId
            ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                state,
                object.riskCategory?.dictId,
                object.riskCategory?.dictVersionId,
              )
            : undefined;
          const dangerClassSelectOption = object.dangerClass?.dictId
            ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.DANGER_CLASS>(
                state,
                object.dangerClass?.dictId,
                object.dangerClass?.dictVersionId,
              )
            : undefined;

          return {
            ...getInitialObject(initialObject),
            tmpErvkCosId: object.tmpErvkCosId || undefined,
            riskCategory: riskCategorySelectOption && {
              ...riskCategorySelectOption,
              inspectionResult: object.riskCategory?.inspectionResult || parentInspectionResultRisk,
            },
            dangerClass: dangerClassSelectOption && {
              ...dangerClassSelectOption,
              inspectionResult: object.dangerClass?.inspectionResult || parentInspectionResultDanger,
            },
            isFilledFromLink: object.isFilledFromLink || undefined,
          };
        }),
      };

      yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      break;
    }
    case DictionaryUUIDsEnum.DANGER_CLASS: {
      const initialObjects = currentKnmData?.pmErknm?.objects;

      const partialInitialFormValues: {
        objectsPm: IKnmPmForm['objectsPm'];
      } = {
        objectsPm: initialObjects?.map((object, index) => {
          const selectOption = object.dangerClass?.dictId
            ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                state,
                object.dangerClass?.dictId,
                object.dangerClass?.dictVersionId,
              )
            : undefined;
          const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsPm?.[index];
          const inspectionResults = object.inspectionResults || [];
          const parentInspectionResultDanger = inspectionResults.find(
            i => i.inspectedField === InspectionResultFieldEnum.DANGER_CLASS,
          );

          return {
            ...getInitialObject(initialObject),
            tmpErvkCosId: object.tmpErvkCosId || undefined,
            dangerClass: selectOption && {
              ...selectOption,
              inspectionResult: object.dangerClass?.inspectionResult || parentInspectionResultDanger,
            },
          };
        }),
      };

      yield put(knmPmSetInitialFormValues(partialInitialFormValues));
      break;
    }
    case DictionaryUUIDsEnum.ACCEPTED_DOCUMENT: {
      const initialApproveDocs = currentKnmData?.pmErknm?.approveDocs;

      yield put(
        knmPmSetInitialFormValues({
          approveDocs: initialApproveDocs?.map(item => {
            const approveDocSelectOption = getDictionarySelectOptionWithState<
              typeof DictionaryUUIDsEnum.ACCEPTED_DOCUMENT
            >(state, item.approveDoc?.dictId, item.approveDoc?.dictVersionId);
            const approveDocFormValue: IKnmErknmFormApproveDoc = {
              approveDoc: approveDocSelectOption && {
                ...approveDocSelectOption,
                inspectionResult: item.approveDoc?.inspectionResult,
              },
              dateCreate: item.dateCreate,
              numGuid: item.numGuid,
              listId: item.numGuid || uuidv4(),
            };

            return approveDocFormValue;
          }),
        }),
      );
      break;
    }
    default:
  }
}

function* knmPmSetValueByOptionsFromCatalogActionsSaga(action: ActionType<typeof catalogGetSuccess>) {
  const { catalogName } = action.payload;
  const state: IRootState = yield select();
  const currentKnmData = getKnmData(state);
  const initialObjects = currentKnmData?.pmErknm?.objects;

  switch (catalogName) {
    case CommonCatalogsNamesEnum.DISTRICTS: {
      let mainRegion: IKnmDistrictSelectOption | undefined;
      let mainFiasRegion: IKnmDistrictSelectOption | undefined;

      const list = getDistrictSelectOptions(state);

      // Смотрим Место проведения профилактического мероприятия
      if (currentKnmData?.pmErknm?.region) {
        mainRegion = list.find(i => i.id.toString() === currentKnmData?.pmErknm?.region);
      }
      if (currentKnmData?.pmErknm?.fiasRegion) {
        mainFiasRegion = list.find(i => i.id.toString() === currentKnmData?.pmErknm?.fiasRegion);
      }

      // Только фолбэк региона по адресу — через API; остальное маппится синхронно,
      // чтобы не затирать тип/вид/подвид/риск, уже заполненные справочниками.
      const regionFallbacks: Array<{
        region?: IKnmDistrictSelectOption;
        fiasRegion?: IKnmDistrictSelectOption;
      }> = yield call(async () => {
        if (!initialObjects?.length) {
          return [];
        }

        return Promise.all(
          initialObjects.map(async obj => {
            if (obj?.region || obj?.fiasRegion) {
              return {};
            }

            if (obj.notInFIAS) {
              return {
                region: await findRegionSelectOption(obj?.address, null, list),
              };
            }

            return {
              fiasRegion: await findRegionSelectOption(obj?.address, null, list),
            };
          }),
        );
      });

      // После await стор мог обновиться — перечитываем, чтобы не брать устаревший state
      const freshState: IRootState = yield select();
      const freshInitialFormValues = getKnmPmInitialFormValues(freshState);

      yield put(
        knmPmSetInitialFormValues({
          region: mainRegion,
          fiasRegion: mainFiasRegion,
          objectsPm: initialObjects?.map((obj, index) => {
            let objRegion: IKnmDistrictSelectOption | undefined;
            let objFiasRegion: IKnmDistrictSelectOption | undefined;

            // Смотрим Местонахождение в сведениях об объектах контроля
            if (obj?.region) {
              if (typeof obj.region === 'object') {
                objRegion = list.find(i => i.id.toString() === (obj.region as IFederalDistrict)?.id?.toString());
              } else {
                objRegion = list.find(i => i.id.toString() === obj?.region);
              }
            }
            if (obj?.fiasRegion) {
              if (typeof obj.fiasRegion === 'object') {
                objFiasRegion = list.find(
                  i => i.id.toString() === (obj.fiasRegion as IFederalDistrict)?.id?.toString(),
                );
              } else {
                objFiasRegion = list.find(i => i.id.toString() === obj?.fiasRegion);
              }
            }
            if (!obj?.region && !obj?.fiasRegion) {
              objRegion = regionFallbacks[index]?.region;
              objFiasRegion = regionFallbacks[index]?.fiasRegion;
            }

            const initialObject: IKnmErknmFormObject | undefined = freshInitialFormValues.objectsPm?.[index];

            return {
              ...getInitialObject(initialObject),
              tmpErvkCosId: obj.tmpErvkCosId || undefined,
              region: objRegion,
              fiasRegion: objFiasRegion,
            };
          }),
        }),
      );
      break;
    }
    default:
  }
}

function* knmPmSetAbrogationReasonSaga(action: ActionType<typeof catalogGetSuccess>) {
  const { catalogName, data } = action.payload;
  const state: IRootState = yield select();
  const initialFormValues = getKnmPmInitialFormValues(state);

  switch (catalogName) {
    case CommonCatalogsNamesEnum.KNM_TYPES_CANCELED: {
      const values = data as IKnmAbrogationReason[];

      if (
        initialFormValues?.abrogation &&
        initialFormValues?.abrogation.reasonId &&
        !initialFormValues?.abrogation.reasonId.label
      ) {
        const reasonId = values.find(i => i.id === initialFormValues?.abrogation?.reasonId?.id);

        yield put(
          knmPmSetInitialFormValues({
            abrogation: {
              ...initialFormValues.abrogation,
              reasonId,
            },
          }),
        );
      }
      break;
    }
    default:
      break;
  }
}

function* knmPmGetPlanKnmSelectOptionsSaga(action: ActionType<typeof knmPmGetPlanKnmSelectOptions>) {
  const { callback, filters } = action.payload;

  // Колбэк вызывается и при ошибке, иначе поле поиска остается в состоянии загрузки
  try {
    const result: IKnmIndexPage = yield fetchKnmFindIndex(0, 100, filters);

    const selectOptions: IKnmPmPlanKnmSelectOption[] = result.list.map(knm => ({
      ...knm,
      value: knm.erpId,
      label: knm.erpId,
    }));

    yield put(knmPmSetPlanKnmSelectOptions(selectOptions));
    callback(selectOptions);
  } catch (err) {
    callback([]);
    console.error('Failed to fetch knm find index');
  }
}

function* knmPmReloadParticipantByCancelSaga() {
  const state: IRootState = yield select();
  const currentKnmData = getKnmData(state);

  if (currentKnmData?.pmErknm?.warningContentPositionCancel) {
    yield getDictionariesItemValue(currentKnmData?.pmErknm?.warningContentPositionCancel);
    yield take([
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_SUCCESS,
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_ALREADY_LOAD,
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_NO_DATA,
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_FAILURE,
    ]);

    const warningContentPositionCancel = currentKnmData?.pmErknm?.warningContentPositionCancel;
    const positions = getKnmErknmPositionsSelectOptions(state);

    let warningContentPositionCancelValue:
      | IDictionaryPositionSelectOption
      | undefined = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.POSITIONS>(
      state,
      warningContentPositionCancel?.dictId,
      warningContentPositionCancel?.dictVersionId,
    );

    if (!warningContentPositionCancelValue) {
      warningContentPositionCancelValue = positions?.find(
        i =>
          i.value.dictVersionId === warningContentPositionCancel?.dictVersionId &&
          i.value.dictId === warningContentPositionCancel?.dictId,
      );
    }
    yield put(
      knmPmSetInitialFormValues({
        warningContentPositionCancel: warningContentPositionCancelValue,
      }),
    );
  }
}

function* knmPmInitKnmPlanByOptionsSaga() {
  const state: IRootState = yield select();
  const currentKnmData = getKnmData(state);
  const initialFormValues = getKnmPmInitialFormValues(state);

  const initialReasons = currentKnmData?.pmErknm?.reasons;
  const initialKnmIdLink = initialFormValues.reasonsPm?.find(value => value.knmIdLink != null)?.knmIdLink;

  if (initialKnmIdLink) {
    const result: IKnmIndex = yield fetchKnm(initialKnmIdLink);

    const selectOption: IKnmPmPlanKnmSelectOption = {
      ...result,
      value: result.erpId,
      label: result.erpId,
    };

    yield put(knmPmSetPlanKnmSelectOptions([selectOption]));

    const getPlanKnmById = (knmIdLink: number | undefined) => {
      if (knmIdLink) {
        return selectOption;
      }
    };

    yield put(
      knmPmSetInitialFormValues({
        reasonsPm: convertInitialReasonsPmToFormDate({
          initialReasons,
          state,
          getPlanKnmById,
          initialFormValues,
        }),
      }),
    );
  }
}

function* knmPmInitProsecutorOrganizationSaga(action: ActionType<typeof knmGetSuccess>) {
  function* funcTry() {
    const { data: currentKnmData } = action.payload;
    const { prosecutorOrganization } = currentKnmData;

    if (prosecutorOrganization?.ervkUuid && prosecutorOrganization?.ervkVersionUuid) {
      const ervkProsecutorOrganization: IUserOrganisation = yield fetchProsecutorOrganisationsByErvkUuidAndVersion(
        prosecutorOrganization?.ervkUuid,
        prosecutorOrganization?.ervkVersionUuid,
      );

      const userOrganizationWithDistrictSelectOption = createUserOrganizationWithDistrictSelectOption(
        ervkProsecutorOrganization,
      );

      yield put(
        knmPmSetInitialFormValues({
          prosecutorOrganizationErknm: userOrganizationWithDistrictSelectOption,
        }),
      );
    } else {
      const initialProsecutorOrganization = currentKnmData?.prosecutorOrganization;
      const initialDistrictName = currentKnmData?.district?.name || null;
      const initialDistrictId = currentKnmData?.district?.id || null;

      yield put(
        knmPmSetInitialFormValues({
          prosecutorOrganizationErknm:
            initialProsecutorOrganization?.id &&
            !initialProsecutorOrganization?.ervkUuid &&
            initialProsecutorOrganization?.name
              ? createKnmProsecutorOrganisationSelectOption({
                  prosecutorOrganization: {
                    ...initialProsecutorOrganization,
                    id: initialProsecutorOrganization.id,
                    name: initialProsecutorOrganization?.name,
                  },
                  districtName: initialDistrictName,
                  districtId: initialDistrictId?.toString() || null,
                })
              : undefined,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmPmFillAgreementProsecutorOrganizationSaga(action: ActionType<typeof knmGetSuccess>) {
  function* funcTry() {
    yield call(createDictionaryWaitChannel);

    const state: IRootState = yield select();
    const { data: currentKnmData } = action.payload;
    const { pmErknm } = currentKnmData;
    const initialFormValues = getKnmPmInitialFormValues(state);

    if (pmErknm?.agreements) {
      const promises: Promise<IUserOrganisationSelectOption | undefined>[] = [];
      const organizations: IProsecutorOffice[] = [];

      // Загрузка данных о прокуратурах из справочников
      pmErknm?.agreements?.forEach(async i => {
        // Загрузка прокуратуры из основных сведений
        if (
          i.prosecutorOrganization &&
          !organizations.find(
            org =>
              (org.ervkUuid === i.prosecutorOrganization?.ervkUuid &&
                org.ervkVersionUuid === i.prosecutorOrganization?.ervkVersionUuid) ||
              org.id === i.prosecutorOrganization?.id,
          )
        ) {
          promises.push(getProsecutorOrganization(i.prosecutorOrganization, currentKnmData?.district));
          organizations.push(i.prosecutorOrganization);
        }
        // Загрузка прокуратуры из сведений о расторжении
        if (
          i.terminate?.prosecutorOrganization &&
          !organizations.find(org =>
            org.ervkUuid
              ? org.ervkUuid === i.terminate?.prosecutorOrganization?.ervkUuid &&
                org.ervkVersionUuid === i.terminate?.prosecutorOrganization?.ervkVersionUuid
              : org.id && org.id === i.terminate?.prosecutorOrganization?.id,
          )
        ) {
          promises.push(getProsecutorOrganization(i.terminate?.prosecutorOrganization, currentKnmData?.district));
          organizations.push(i.terminate?.prosecutorOrganization);
        }
      });

      const results: (IUserOrganisationSelectOption | undefined)[] = yield call(() => Promise.all(promises));

      const agreementsForm = initialFormValues.agreements?.map(agreement => {
        const findOrg = (prosecutorOrganization: IUserOrganisationSelectOption | undefined) =>
          results.find(
            org =>
              org &&
              (org.ervkUuid
                ? org.ervkUuid === prosecutorOrganization?.ervkUuid &&
                  org.ervkVersionUuid === prosecutorOrganization?.ervkVersionUuid
                : org.id && org.id === prosecutorOrganization?.id),
          );

        return {
          ...agreement,
          prosecutorOrganization: findOrg(agreement.prosecutorOrganization),
          terminate: !agreement.terminate
            ? undefined
            : {
                ...agreement.terminate,
                prosecutorOrganization: findOrg(agreement.terminate?.prosecutorOrganization),
              },
        } as IErknmFormAgreement;
      });

      yield put(
        knmPmSetInitialFormValues({
          agreements: agreementsForm,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

const fillObj = (pmErknm: IPm | null | undefined) => {
  const resultObj = {};

  if (!pmErknm) return resultObj as IPm;

  Object.keys(pmErknm).forEach(key => {
    // @ts-ignore
    resultObj[key] = pmErknm[key] || null;
  });

  return resultObj as IPm;
};

function* pmErknmFillOrganizationsAndObjectsByOtherPassportSaga(
  action: ActionType<typeof knmPmFillOrganizationsAndObjectsByOtherPassport>,
) {
  const state: IRootState = yield select();
  const formRef = getKnmFormRef(state);
  const currentKnmData = getKnmData(state);
  const currentFormData = formRef?.current.getState().values;
  const initialFormValues = getKnmPmInitialFormValues(state);
  const { formOrganizations } = yield getOrganizations(
    currentKnmData?.id,
    currentKnmData?.created,
    action.payload.value.organizations.map(org => ({ ...org, isFilledFromLink: true })),
    state,
  );
  const list = getDistrictSelectOptions(state);
  const currentDictionariesFromData: IKnmDictionary[] = [];
  const objects: IKnmErknmFormObject[] = [];

  action.payload.value.objects?.forEach(object => {
    if (object.objectType) {
      currentDictionariesFromData.push(object.objectType);
    }
    if (object.objectKind) {
      currentDictionariesFromData.push(object.objectKind);
    }
    if (object.objectSubKind) {
      currentDictionariesFromData.push(object.objectSubKind);
    }
    if (object.riskCategory) {
      currentDictionariesFromData.push(object.riskCategory);
    }
    if (object.dangerClass) {
      currentDictionariesFromData.push(object.dangerClass);
    }
    let objRegion: IKnmDistrictSelectOption | undefined;
    let objFiasRegion: IKnmDistrictSelectOption | undefined;

    // Смотрим Местонахождение в сведениях об объектах контроля
    const extractId = (value: string | IFederalDistrict | null): string | undefined => {
      if (!value) return undefined;

      return typeof value === 'string' ? value : value.id?.toString();
    };

    if (object?.region) {
      objRegion = list.find(i => i.id.toString() === extractId(object.region));
    }

    if (object?.fiasRegion) {
      objFiasRegion = list.find(i => i.id.toString() === extractId(object.fiasRegion));
    }

    const objectTypeSelectOption = object.objectType?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_TYPE>(
          state,
          object.objectType?.dictId,
          object.objectType?.dictVersionId,
        )
      : undefined;
    const objectKindSelectOption = object.objectKind?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_KIND>(
          state,
          object.objectKind?.dictId,
          object.objectKind?.dictVersionId,
        )
      : undefined;
    const objectSubKindSelectOption = object.objectSubKind?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_KIND>(
          state,
          object.objectSubKind?.dictId,
          object.objectSubKind?.dictVersionId,
        )
      : undefined;
    const riskCategorySelectOption = object.riskCategory?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.RISK_CATEGORY>(
          state,
          object.riskCategory?.dictId,
          object.riskCategory?.dictVersionId,
        )
      : undefined;
    const dangerClassSelectOption = object.dangerClass?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.DANGER_CLASS>(
          state,
          object.dangerClass?.dictId,
          object.dangerClass?.dictVersionId,
        )
      : undefined;

    objects.push({
      region: objRegion,
      fiasRegion: objFiasRegion,
      objectType: objectTypeSelectOption,
      objectKind: objectKindSelectOption,
      objectSubKind: objectSubKindSelectOption,
      riskCategory: riskCategorySelectOption,
      dangerClass: dangerClassSelectOption,
      address: object.address || undefined,
      guid: object.guid || undefined,
      ervkNumber: object.ervkNumber || undefined,
      ervkId: object.ervkId || undefined,
      cosId: object.cosId || undefined,
      cosVersion: object.cosVersion || undefined,
      name: object.name || undefined,
      streetGuid: object.streetGuid || undefined,
      houseGuid: object.houseGuid || undefined,
      roomGuid: object.roomGuid || undefined,
      steadGuid: object.steadGuid || undefined,
      addressFns: object.addressFns || undefined,
      autocomplete: typeof object.autocomplete === 'boolean' ? object.autocomplete : undefined,
      dateCreate: object.dateCreate || undefined,
      geolocation: object.geolocation || undefined,
      listId: object.guid || uuidv4(),
      isNew: false,
      manualAddress: object.manualAddress || undefined,
      notInFIAS: object.notInFIAS || undefined,
      regionName: object.regionName || undefined,
      isErrorFiasValue: undefined,
      inspectionResults: object.inspectionResults || undefined,
      organizationErvkId: object.organizationErvkId || undefined,
      updating: object.updating || undefined,
      isFilledFromLink: object.isFilledFromLink || undefined,
    });
  });

  const requirementsNpaValue = (action.payload.value.requirements as IKnmRequirementsNpa[])?.map(item => ({
    guid: uuidv4(),
    npa: {
      id: item.npaId,
      number: item.number || undefined,
      acceptanceDate: formatDate({
        date: item.acceptanceDate ?? '',
        dateFormat: DATE_FORMAT_LONG_FNS,
        parseFormat: SYSTEM_DATE_FORMAT_FNS,
      }),
      title: item.title || undefined,
    },
    structuralUnit: {
      id: item.structuralUnit.id,
      title: item.structuralUnit.title || undefined,
    },
    mandatory: item.structuralUnit.mandatoryRequirements
      ? item.structuralUnit.mandatoryRequirements.map(mandatory => ({
          id: mandatory.id,
          title: mandatory.title || '',
        }))
      : [],
    inspectionResults: item.inspectionResults,
    isFilledFromLink: item.isFilledFromLink || undefined,
  }));

  const initialEvents = action.payload.value.events;

  const events = initialEvents?.map((item, index) => {
    if (item.event) currentDictionariesFromData.push(item.event);

    const selectOption = item.event?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.EVENT_TYPE>(
          state,
          item.event?.dictId,
          item.event?.dictVersionId,
        )
      : undefined;
    const formEvent: IKnmErknmFormEvent = {
      type: selectOption,
      ...(!selectOption && initialFormValues.eventsPm?.[index]),
      guid: item.guid,
      startDate: convertInitialKnmDateToFormDate(item.startDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
      stopDate: convertInitialKnmDateToFormDate(item.stopDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
      dateCreate: item.dateCreate,
      listId: item?.guid || uuidv4(),
      interactionCategory: item?.interactionCategory
        ? createInteractionCategorySelectOption(item.interactionCategory)
        : undefined,
      isNew: false,
      originalStopDate: item.originalStopDate || undefined,
      submittedDocuments: createSubmittedDocuments(item.submittedDocuments),
      isFilledFromLink: item.isFilledFromLink || undefined,
      eventCopyNumber: item.eventCopyNumber || undefined,
      experts: undefined,
    };

    return formEvent;
  });

  const firstOrganization = action.payload.value?.organizations[0];

  if (currentFormData && currentFormData.reasonsPm) {
    let data = {
      ...currentFormData,
      objectsPm: objects,
      eventsPm: events,
      requirementsNpa: requirementsNpaValue,
      reasonsPm: [
        {
          ...currentFormData.reasonsPm[0],
          links: [action.payload.link],
        },
      ],
    };

    if (action.payload.value.organizations.length > 1) {
      data = {
        ...data,
        organizationsForSelect: formOrganizations,
      };
    } else {
      const districtList = getDistrictSelectOptions(state);
      const locationregion = (yield call(async () => {
        if (!firstOrganization.locationNotInFIAS || !firstOrganization.locationAddress) {
          return undefined;
        }

        return findRegionSelectOption(
          firstOrganization.locationAddress,
          firstOrganization.locationRegionCodeFromFns || undefined,
          districtList,
        );
      })) as IKnmDistrictSelectOption | undefined;
      const locationfiasRegion = (yield call(async () => {
        if (firstOrganization.locationNotInFIAS || !firstOrganization.locationAddress) {
          return undefined;
        }

        return findRegionSelectOption(
          firstOrganization.locationAddress,
          firstOrganization.locationRegionCodeFromFns || firstOrganization.locationFiasRegion || undefined,
          districtList,
        );
      })) as IKnmDistrictSelectOption | undefined;

      data = {
        ...data,
        ...convertSubjectDataToFormValues(firstOrganization),
        locationregion,
        locationfiasRegion,
      };
    }

    yield put(knmPmSetInitialFormValues(data));
  }

  const data: Partial<IKnm> = {
    pmErknm: {
      ...fillObj(currentKnmData?.pmErknm),
      objects: action.payload.value.objects,
      requirements: (action.payload.value.requirements as IKnmRequirementsNpa[] | null) ?? null,
    },
  };

  if (action.payload.value.events && data.pmErknm) {
    data.pmErknm.events = action.payload.value.events;
  }
  yield put(knmPatchRawData(data));

  const resultArrayActions: unknown[] = [];

  uniq(currentDictionariesFromData).forEach(item => resultArrayActions.push(getDictionariesItemValue(item)));

  const isHasNonErvkObjects: boolean = !!objects?.filter(obj => !obj.cosId && !obj.ervkNumber).length;
  const isHasNonErvkSubjects: boolean = !firstOrganization?.ervkId;
  const reasonTypeName: string | undefined = currentKnmData?.pmErknm?.reasons?.[0].reason?.type?.name;

  if (
    (reasonTypeName === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS ||
      reasonTypeName === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK) &&
    (isHasNonErvkSubjects || isHasNonErvkObjects)
  ) {
    yield put(knmAddVisibleModal(KnmModalsEnum.ERKNM_LINK_OTHER_WARNING_MODAL));
  }

  yield all(resultArrayActions);
}

function* pmCreateFromModalSaga(action: ActionType<typeof knmPmCreateFromModal>): Generator {
  try {
    const { values, history } = action.payload;
    const dto = convertCreateModalPreventionToDTO(values);

    const res = yield fetchCreatePmFromModalForm(dto);

    history.push(ROUTE_PM(Number(res)));
  } catch (err) {
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.CREATE_PM,
        renderContent: 'Ошибка создания ПМ',
      }),
    );
  }
}

function* pmErknmSetChoosenOrganizationsByOtherPassportSaga(
  action: ActionType<typeof knmPmSetChoosenOrganizationsByOtherPassport>,
) {
  const state: IRootState = yield select();
  const formRef = getKnmFormRef(state);
  const currentFormData = formRef?.current.getState().values;

  const item = action.payload.organization;

  if (currentFormData) {
    const organization: IKnmSubject248 = {
      inn: item.inn || null,
      ogrn: item.ogrn || null,
      position: null,
      okveds: convertKnmOkvedsFormValueToKnmData(item.okveds),
      addressFns: item.addressFns || null,
      organizationName: item.organizationName || null,
      firstOrganizationName: item.firstOrganizationName || null,
      mspCode: item.mspCode || null,
      checkSubjectResult: item.checkSubjectResult || null,
      dateCreate: item.dateCreate || null,
      act: null,
      resultDecisions: null,
      isAutocomplete: typeof item.isAutocomplete === 'boolean' ? item.isAutocomplete : null,
      isFiz: typeof item.isFiz === 'boolean' ? item.isFiz : null,
      guid: item.guid || null,
      isDocumentOrganization: item.isDocumentOrganization,
      nkoForeignAgentsRegistryNumber: item.nkoForeignAgentsRegistryNumber || null,
      nkoForeignAgentsRegistryInclusionDate: item.nkoForeignAgentsRegistryInclusionDate || null,
      nkoForeignAgentsRegistryExclusionDate: item.nkoForeignAgentsRegistryExclusionDate || null,
      isNko: !!item.nkoForeignAgentsRegistryInclusionDate,
      subjectType: item.subjectType || null,
      rafpNza: item.rafpNza || null,
      smevRequestStatus: item.smevRequestStatus || null,
      kpp: item.kpp || null,
      isForeignSubjectAgencyNotRegistredInRf: item.isForeignSubjectAgencyNotRegistredInRf || null,
      country: item.country || null,
      isSonko: typeof item.isSonko === 'boolean' ? item.isSonko : null,
      locationAddress: item.locationAddress || null,
      workAddress: item.workAddress || null,
      factualAddress: item.factualAddress || null,
      snils: item.snils || null,
      document: item.document || null,
      epguNotifications: item.epguNotifications || null,
      ervkId: item.ervkId || null,
      isPresentInItRegistry: typeof item.isPresentInItRegistry === 'boolean' ? item.isPresentInItRegistry : null,
      itAccreditationResponse: item.itAccreditationResponse || null,
      smevItAccreditationRequestStatus: item.smevItAccreditationRequestStatus || null,
      isFilledFromLink: true,
      locationRegion:
        // eslint-disable-next-line no-nested-ternary
        item.locationregion && 'id' in item.locationregion
          ? item.locationregion.id.toString()
          : typeof item.locationregion === 'string'
          ? item.locationregion
          : null,
      locationNotInFIAS: item.locationnotInFIAS || null,
      locationManualAddress: item.locationmanualAddress || null,
      locationFiasRegion:
        // eslint-disable-next-line no-nested-ternary
        item.locationfiasRegion && 'id' in item.locationfiasRegion
          ? item.locationfiasRegion.id.toString()
          : typeof item.locationfiasRegion === 'string'
          ? item.locationfiasRegion
          : null,
      locationRegionCodeFromFns: item.locationRegionCodeFromFns || null,
    };

    const organiz = convertSubjectDataToFormValues(organization);

    const data = {
      ...currentFormData,
      organizationsForSelect: undefined,
      wasSelectedOrganizationByOtherPassport: true,
      ...organiz,
    };

    yield put(knmPmSetInitialFormValues(data));
  }
}
